import React from 'react';
import { Loader2, FileText, XCircle } from 'lucide-react';
import { useHolisticStore } from '../../stores/useHolisticStore';
import { useGraphStore } from '../../stores/useGraphStore';
import { openHolisticReportInNewTab } from './HolisticTraceReport';
import { REPORT_TYPES, buildReportTitle } from '../../utils/reportArtifacts';
import { IntelligenceNoteView } from '../ui/IntelligenceNoteView';

/** Build {address: label} map from current canvas elements for the seeds. */
function collectAddressLabels(addrs: string[]): Record<string, string> {
    const elements = useGraphStore.getState().elements;
    const out: Record<string, string> = {};
    for (const addr of addrs) {
        const el = elements.find(e => !e.data?.source && e.data?.id === addr);
        const name = el?.data?.entity_name || el?.data?.label;
        if (name && typeof name === 'string') out[addr] = name;
    }
    return out;
}

/**
 * Capture the full graph as a base64 PNG via the canonical `window.__cy`
 * accessor (same pattern as Follow-the-Money report). Returns undefined if
 * cy isn't ready or the capture throws — the report degrades gracefully.
 */
function captureCanvasSnapshot(): string | undefined {
    try {
        const cy = (window as any).__cy;
        if (!cy || typeof cy.png !== 'function') return undefined;
        return cy.png({ output: 'base64uri', full: true, scale: 1.5, bg: '#ffffff' });
    } catch {
        return undefined;
    }
}

/**
 * Read-only display of the most recent holistic trace run.
 * State lives in useHolisticStore — no props needed.
 * Rendered in both MultiSelectView and AnalysisTab.
 * Case-file auto-archive runs in Workbench (store-driven) so it still saves
 * when this card is unmounted / accordion collapsed.
 */
interface HolisticTraceCardProps {
    /** Archive the rendered report under the case/investigation (ISSUE-02). */
    onPersistReport?: (payload: { title: string; htmlContent: string; metadata?: Record<string, any> }) => Promise<void> | void;
}

export const HolisticTraceCard: React.FC<HolisticTraceCardProps> = ({ onPersistReport }) => {
    const { status, chain, addresses, currentStep, results, error } = useHolisticStore();
    const crossLinks = results.cross_link_discovery?.cross_links_found ?? 0;
    const sharedEntities = results.shared_entity_identification?.shared_entities?.length ?? 0;

    if (status === 'idle') {
        return (
            <div className="p-4 text-xs text-muted-foreground italic">
                No trace run yet. Select 2+ addresses and click <strong>Holistic</strong> in the toolbar.
            </div>
        );
    }

    return (
        <div className="p-4 flex flex-col gap-3">
            <div className="text-xs text-muted-foreground">
                {addresses.length} addresses · {chain}
            </div>

            {status === 'running' && (
                <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-md border border-border">
                    <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />
                    <span className="text-sm font-medium">
                        {currentStep.replace(/_/g, ' ')}
                    </span>
                </div>
            )}

            {status === 'error' && error && (
                <div className="flex items-center gap-3 p-3 bg-red-500/10 text-red-500 rounded-md border border-red-500/20">
                    <XCircle className="w-4 h-4 shrink-0" />
                    <span className="text-sm">{error}</span>
                </div>
            )}

            {Object.keys(results).length > 0 && (
                <div className="space-y-2">
                    <span className="text-[11px] text-muted-foreground">
                        {crossLinks} cross-link{crossLinks === 1 ? '' : 's'}
                        {' · '}
                        {sharedEntities} shared entit{sharedEntities === 1 ? 'y' : 'ies'}
                    </span>

                    {results.ai_analysis?.narrative && (
                        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-3">
                            <IntelligenceNoteView
                                markdown={results.ai_analysis.narrative}
                                eyebrow="Multi-node trace"
                                maxHeightClass="max-h-none"
                            />
                        </div>
                    )}

                    {status === 'complete' && (
                        <button
                            onClick={() => {
                                openHolisticReportInNewTab(
                                    chain, addresses, results, undefined,
                                    collectAddressLabels(addresses), captureCanvasSnapshot(),
                                    onPersistReport
                                        ? {
                                            onPersist: (html) => onPersistReport({
                                                title: buildReportTitle(REPORT_TYPES.holistic, {
                                                    chain,
                                                    addresses,
                                                }),
                                                htmlContent: html,
                                                metadata: {
                                                    report_kind: 'holistic',
                                                    chain,
                                                    addresses,
                                                    address: addresses[0],
                                                    archive_source: 'view_le_report',
                                                },
                                            }),
                                        }
                                        : undefined,
                                );
                                // C5/F4: a holistic report covers every seed — mark each.
                                addresses.forEach(a => useGraphStore.getState().markNodeReported(a, 'holistic'));
                            }}
                            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-brand-gold-hover text-primary-foreground text-white py-2 rounded-md transition-colors text-sm mt-1"
                        >
                            <FileText className="w-4 h-4" />
                            View LE Report
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
